// src/app/top-bar-search.tsx
// The top bar's quiet patient search (design log 12–13 Oct, "Top bar: search" and the practice
// GlobalBar rule): 400px wide (`--search-width`), shrinking first on narrow windows (down to 200);
// a hint-colour placeholder that says what this screen's search finds, drawn as a text layer so a
// band of white light can swipe across it once when the page changes (reduced motion: no swipe;
// screen readers keep the real placeholder); ⌘K in a hairline outline, and ⌘K focuses the field.
// Enter opens the patient list already filtered (the term travels in navigation state, never in
// the URL); Escape clears. Looks: src/styles/showcase-bridge.css under `/* top bar */`.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { searchPlaceholder } from './top-bar-routes';
import { Search } from 'lucide-react';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function TopBarSearch() {
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState('');
  const placeholder = searchPlaceholder(pathname);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div role="search" data-slot="top-bar-search" className="topbar-search">
      <Search className="size-4 topbar-search-icon" aria-hidden="true" />
      <span className="topbar-search-field">
        {!q && (
          <span aria-hidden="true" className="topbar-search-ph">
            {/* Keyed by the text, so a new placeholder replays the swipe of light. */}
            <span key={placeholder}>{placeholder}</span>
          </span>
        )}
        <input
          ref={inputRef}
          type="search"
          aria-label="Search patients"
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && q.trim()) {
              e.preventDefault();
              navigate('/patients', { state: { search: q.trim() } });
              setQ('');
              inputRef.current?.blur();
            } else if (e.key === 'Escape') {
              setQ('');
              inputRef.current?.blur();
            }
          }}
          className="topbar-search-input"
        />
      </span>
      <kbd className="topbar-search-kbd">{IS_MAC ? '⌘K' : 'Ctrl K'}</kbd>
    </div>
  );
}
